// src/components/Podcasts.js
import { useState, useEffect } from 'react';
import SpotifyWebApi from 'spotify-web-api-js';
import Navbar from './Navbar';

const spotifyApi = new SpotifyWebApi();

const Podcasts = ({ token }) => {
  const [shows, setShows] = useState([]);
  const [selectedShow, setSelectedShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('shows');

  // Fetch podcasts on component mount
  useEffect(() => {
    const fetchShows = async () => {
      if (!token) return;
      try {
        setLoading(true);
        spotifyApi.setAccessToken(token);
        const response = await spotifyApi.getMySavedShows({ limit: 50 });
        setShows(response.items);
      } catch (err) {
        setError('Failed to fetch podcasts');
        console.error('Error fetching podcasts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchShows();
  }, [token]);

  // Fetch episodes when a show is selected
  useEffect(() => {
    const fetchEpisodes = async () => {
      if (!selectedShow) return;
      try {
        setLoading(true);
        const response = await spotifyApi.getShowEpisodes(selectedShow.show.id);
        setEpisodes(response.items);
      } catch (err) {
        setError('Failed to fetch episodes');
        console.error('Error fetching episodes:', err);
      } finally {
        setLoading(false);
      }
    };

    if (selectedShow) {
      fetchEpisodes();
    }
  }, [selectedShow]);

  const handlePlayShow = async (showUri) => {
    try {
      await spotifyApi.play({
        context_uri: showUri
      });
    } catch (err) {
      setError('Failed to play podcast. Make sure you have a Premium account and an active device.');
      console.error('Error playing show:', err);
    }
  };

  const handlePlayEpisode = async (episodeUri) => {
    try {
      await spotifyApi.play({
        uris: [episodeUri]
      });
    } catch (err) {
      setError('Failed to play episode. Make sure you have a Premium account and an active device.');
      console.error('Error playing episode:', err);
    }
  };

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    if (minutes > 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours} hr ${remainingMinutes} min`;
    }
    return `${minutes} min`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-9 p-4">
            <div className="d-flex align-items-center">
              <h2 className="me-3">Loading...</h2>
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container-fluid">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

      <div className="row">
        <div className="col-md-9 p-4">
          {viewMode === 'shows' ? (
            <>
              <h2 className="mb-4">Your Podcasts</h2>
              <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-4">
                {shows.map(({ show }) => (
                  <div key={show.id} className="col">
                    <div className="card h-100 border-0 shadow-sm">
                      {/* Square image container */}
                      <div className="position-relative" style={{ width: '100%', paddingBottom: '100%' }}>
                        {show.images?.[0] ? (
                          <img
                            src={show.images[0].url}
                            alt={show.name}
                            className="position-absolute top-0 start-0 w-100 h-100"
                            style={{ objectFit: 'contain' }}
                          />
                        ) : (
                          <div className="position-absolute top-0 start-0 w-100 h-100 bg-dark d-flex align-items-center justify-content-center">
                            <i className="bi bi-mic text-light" style={{ fontSize: '2rem' }}></i>
                          </div>
                        )}
                      </div>
                      <div className="card-body">
                        <h5 className="card-title text-truncate mb-1">{show.name}</h5>
                        <p className="card-text">
                          <small className="text-muted">
                            {show.publisher}<br />
                            {show.total_episodes} episodes
                          </small>
                        </p>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-success btn-sm flex-grow-1"
                            onClick={() => handlePlayShow(show.uri)}
                          >
                            <i className="bi bi-play-fill"></i> Play
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm flex-grow-1"
                            onClick={() => {
                              setSelectedShow({ show });
                              setViewMode('episodes');
                            }}
                          >
                            View Episodes
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div className="d-flex gap-4 align-items-center">
                  {selectedShow?.show.images?.[0] && (
                    <img
                      src={selectedShow.show.images[0].url}
                      alt={selectedShow.show.name}
                      style={{ width: '150px', height: '150px' }}
                      className="rounded shadow-sm"
                    />
                  )}
                  <div>
                    <h2 className="mb-0">{selectedShow?.show.name}</h2>
                    <p className="text-muted mb-0">
                      By {selectedShow?.show.publisher} • 
                      {' '}{selectedShow?.show.total_episodes} episodes
                    </p>
                  </div>
                </div>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setViewMode('shows');
                    setSelectedShow(null);
                  }}
                >
                  Back to Podcasts
                </button>
              </div>

              <div className="list-group">
                {episodes.map((episode) => (
                  <button
                    key={episode.id}
                    className="list-group-item list-group-item-action d-flex align-items-center gap-3 p-3"
                    onClick={() => handlePlayEpisode(episode.uri)}
                  >
                    <div className="flex-shrink-0" style={{ width: '80px', height: '80px' }}>
                      {episode.images?.[0] && (
                        <img
                          src={episode.images[0].url}
                          alt={episode.name}
                          className="w-100 h-100"
                          style={{ objectFit: 'cover' }}
                        />
                      )}
                    </div>
                    <div className="d-flex flex-column flex-grow-1">
                      <strong className="mb-1">{episode.name}</strong>
                      <small className="text-muted mb-2">
                        {episode.description.length > 150 
                          ? `${episode.description.substring(0, 150)}...` 
                          : episode.description}
                      </small>
                      <div className="d-flex gap-2 text-muted">
                        <small>{formatDate(episode.release_date)}</small>
                        <small>•</small>
                        <small>{formatDuration(episode.duration_ms)}</small>
                      </div>
                    </div>
                    <div className="text-success">
                      <i className="bi bi-play-circle fs-4"></i>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default Podcasts;